import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ChangeEvent, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { api, errorMessage, type Schemas, unwrap } from "@/api/client";
import { RUNS_KEY } from "@/api/runs";
import { CheckRow, Field, NumberRow } from "@/components/form-rows";
import Icon from "@/components/icon";
import { List, ListHeader, ListItem, ListItemContent, ListItemTrailing, ListTitle } from "@/components/list";
import PageHeader from "@/components/page-header";
import Text from "@/components/text";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

import { BuyNumbers } from "./BuyRun";

type Settings = Omit<Schemas["CreateRunRequest"], "name" | "text" | "password">;

// Register runs only for now: the login mode is hidden (the backend still supports it).
// Most rental numbers need an email before Telegram sends a code, so auto email is on.
const DEFAULTS: Required<Settings> = {
  submit_type: "register",
  concurrency: 5,
  stagger_seconds: 5,
  send_rate_per_second: 1,
  max_attempts: 3,
  failure_limit: 10,
  prefix: "",
  auto_email: true,
  skip_settled: true,
  otp_poll_interval: 2.5,
  otp_poll_timeout: 20,
};

function defaultName(): string {
  const d = new Date();
  return `Run ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
}

export function NewRunPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const buying = params.get("source") === "buy";
  const setSource = (v: string) => {
    const next = new URLSearchParams(params);
    if (v === "buy") next.set("source", "buy");
    else next.delete("source");
    setParams(next, { replace: true });
  };
  const [name, setName] = useState(defaultName);
  const [text, setText] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [s, setS] = useState<Required<Settings>>(DEFAULTS);
  const set = <K extends keyof Settings>(key: K, value: Required<Settings>[K]) =>
    setS((prev) => ({ ...prev, [key]: value }));

  const offer = useQuery({
    queryKey: ["auto-email-offer"],
    queryFn: () => unwrap(api.POST("/api/settings/test-key")),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const offerData = offer.data?.ok ? offer.data.auto_email : null;

  // A check only counts for the exact inputs it was made from.
  const previewInput = useMemo(
    () => JSON.stringify({ text, prefix: s.prefix, skip_settled: s.skip_settled }),
    [text, s.prefix, s.skip_settled],
  );
  const preview = useMutation({
    mutationFn: (input: string) =>
      unwrap(api.POST("/api/runs/preview", { body: JSON.parse(input) as Schemas["PreviewRequest"] })),
  });
  const current = preview.data && preview.variables === previewInput ? preview.data : null;

  const start = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/runs", {
          body: {
            ...s,
            name: name.trim(),
            text,
          },
        }),
      ),
    onSuccess: (run) => {
      void queryClient.invalidateQueries({ queryKey: RUNS_KEY });
      toast.success(`Started with ${run.counts.total} numbers`);
      navigate(`/runs/${run.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setText(await file.text());
    setFileName(file.name);
    e.target.value = "";
  };

  const lines = text ? text.split(/\r?\n/).filter((l) => l.trim()).length : 0;
  const rejected = current?.rejected.filter((r) => !r.settled) ?? [];
  const skipped = current?.rejected.filter((r) => r.settled) ?? [];

  const header = (
    <>
      <PageHeader
        title="New run"
        description={
          buying
            ? "Buy numbers from your provider and register accounts on them."
            : "Add numbers, check them, then start."
        }
      />
      <Tabs value={buying ? "buy" : "paste"} onValueChange={(v) => setSource(v)}>
        <TabsList className="w-fit">
          <TabsTrigger value="paste" className="gap-1.5 px-3 normal-case">
            <Icon name="document" size={16} />
            Paste numbers
          </TabsTrigger>
          <TabsTrigger value="buy" className="gap-1.5 px-3 normal-case">
            <Icon name="cart" size={16} />
            Buy numbers
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </>
  );

  if (buying) {
    return (
      <>
        {header}
        <BuyNumbers name={name} onName={setName} />
      </>
    );
  }

  return (
    <>
      {header}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_400px]">
        <List>
          <ListHeader>
            <ListTitle>Numbers</ListTitle>
            <Button variant="outline" size="sm" asChild>
              <label className="cursor-pointer">
                <Icon name="upload" size={16} />
                Load file
                <input type="file" accept=".txt,.csv,text/plain" onChange={onFile} hidden />
              </label>
            </Button>
          </ListHeader>
          <div className="flex flex-col gap-2 px-(--list-inset) pb-4">
            <Label htmlFor="numbers" className="sr-only">
              Numbers
            </Label>
            <Textarea
              id="numbers"
              spellCheck={false}
              value={text}
              className="h-[26rem] resize-y font-mono text-[0.8rem] leading-6"
              placeholder={
                "One number per line, then its code page:\n\n+1 555 0100 | https://sms-rental.example/view/abc123\n15550101 https://sms-rental.example/view/def456"
              }
              onChange={(e) => {
                setText(e.target.value);
                setFileName(null);
              }}
            />
            <Text.Hint size="caption" className="ps-1">
              {lines} {lines === 1 ? "line" : "lines"}
              {fileName ? ` from ${fileName}` : ""}. Lines starting with # are skipped.
            </Text.Hint>
          </div>
        </List>

        <List className="self-start pb-2">
          <ListHeader>
            <ListTitle>Settings</ListTitle>
          </ListHeader>

          <Field label="Run name">
            {(id) => <Input id={id} value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />}
          </Field>

          <NumberRow
            label="Numbers at once"
            value={s.concurrency}
            min={1}
            max={100}
            step={1}
            onChange={(v) => set("concurrency", v)}
          />
          <NumberRow
            label="Tries per number"
            hint="Only for failures that may pass next time"
            value={s.max_attempts}
            min={1}
            max={10}
            step={1}
            onChange={(v) => set("max_attempts", v)}
          />
          <NumberRow
            label="Stop after repeated failures"
            hint="Set 0 to disable"
            value={s.failure_limit}
            min={0}
            max={1000}
            step={1}
            onChange={(v) => set("failure_limit", v)}
          />
          <NumberRow
            label="Stagger, seconds"
            hint="Random pause before each number"
            value={s.stagger_seconds}
            min={0}
            max={600}
            step={0.5}
            onChange={(v) => set("stagger_seconds", v)}
          />
          <NumberRow
            label="Codes per second"
            hint="The server's own limit still applies"
            value={s.send_rate_per_second}
            min={0.05}
            max={50}
            step={0.05}
            onChange={(v) => set("send_rate_per_second", v)}
          />
          <NumberRow
            label="Code wait, seconds"
            value={s.otp_poll_timeout}
            min={5}
            max={270}
            step={1}
            onChange={(v) => set("otp_poll_timeout", v)}
          />
          <ListItem>
            <ListItemContent>
              <Label htmlFor="prefix" className="font-normal">
                Prefix
              </Label>
              <Text.Hint size="caption">Added in front of every number</Text.Hint>
            </ListItemContent>
            <ListItemTrailing>
              <Input
                id="prefix"
                className="h-(--control-height-sm) w-24 text-end"
                value={s.prefix}
                placeholder="none"
                maxLength={8}
                onChange={(e) => set("prefix", e.target.value.replace(/[^\d+]/g, ""))}
              />
            </ListItemTrailing>
          </ListItem>

          <CheckRow
            label="Auto email"
            hint={
              offerData?.available
                ? `Rent the email Telegram asks for, ${offerData.price ?? 0} off each account's price`
                : offer.isPending
                  ? "Checking the offer…"
                  : offer.data && !offer.data.ok
                    ? "Couldn't check: the API key was refused. Fix it in Settings."
                    : offer.isError
                      ? "Couldn't check the offer right now"
                      : "Not offered for your key"
            }
            checked={s.auto_email}
            onChange={(v) => set("auto_email", v)}
          />
          <CheckRow
            label="Skip settled numbers"
            hint="Already registered, or failed for good, in an earlier run"
            checked={s.skip_settled}
            onChange={(v) => set("skip_settled", v)}
          />
        </List>
      </div>

      <List>
        <ListHeader className="flex-wrap gap-3 pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <ListTitle>Check</ListTitle>
            {current && (
              <>
                <Badge className="bg-success text-white">{current.ready} ready</Badge>
                {rejected.length > 0 && <Badge className="bg-danger text-white">{rejected.length} rejected</Badge>}
                {skipped.length > 0 && (
                  <Badge className="bg-muted text-muted-foreground">{skipped.length} already done</Badge>
                )}
                {Object.keys(current.countries).length > 0 && (
                  <Text.Hint size="caption">
                    {Object.entries(current.countries)
                      .map(([cc, n]) => `${cc} ${n}`)
                      .join(", ")}
                  </Text.Hint>
                )}
              </>
            )}
            {preview.data && !current && <Text.Warning size="caption">Changed since the last check</Text.Warning>}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={!text.trim()}
              loading={preview.isPending}
              onClick={() => preview.mutate(previewInput)}
            >
              {current ? "Check again" : "Check lines"}
            </Button>
            <Button
              disabled={!current || current.ready === 0 || !name.trim()}
              loading={start.isPending}
              onClick={() => start.mutate()}
            >
              Start run{current ? ` with ${current.ready}` : ""}
            </Button>
          </div>
        </ListHeader>

        {preview.isError && <Text.Danger className="px-(--list-inset) pb-4">{errorMessage(preview.error)}</Text.Danger>}
        {!current && !preview.isError && (
          <Text.Hint as="p" className="px-(--list-inset) pb-4">
            Check the lines to see what will run and what won't.
          </Text.Hint>
        )}
        {current && current.rejected.length === 0 && (
          <Text.Hint as="p" className="px-(--list-inset) pb-4">
            Every line is good.
          </Text.Hint>
        )}
        {current && current.rejected.length > 0 && (
          <div className="max-h-80 overflow-auto border-t">
            <Table>
              <TableHeader className="sticky top-0 bg-card">
                <TableRow>
                  <TableHead className="w-16 text-end">Line</TableHead>
                  <TableHead>Input</TableHead>
                  <TableHead>Why it's left out</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {current.rejected.map((r) => (
                  <TableRow key={r.line_no}>
                    <TableCell className="text-end text-muted-foreground">{r.line_no}</TableCell>
                    <TableCell className="font-mono text-[0.8rem] break-all whitespace-pre-wrap">
                      {r.raw_line}
                    </TableCell>
                    <TableCell>
                      {r.settled ? <Text.Hint>{r.reason}</Text.Hint> : <Text.Danger>{r.reason}</Text.Danger>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </List>
    </>
  );
}
